import { useEffect, useRef, useState, type ReactNode } from 'react';
import { sx } from './designStyles';

/**
 * The V9 "More" dropdown at the end of a page's tab row (design v9Ux more(), change log
 * "V9 started"): the tabs used every day stay in the row, the rest sit in this menu. One row
 * only. The trigger reads "More", or "More: {tab}" while one of its tabs is open, and then
 * takes the open tab's style.
 */
export interface MoreItem<T extends string> {
  id: T;
  label: string;
  /** A count after the label (Capture's tab counts), with its colour. */
  count?: number | null;
  countColor?: string;
}

/** A menu row (design: 40px tall, 16px, the open one bold blue on a blue wash). */
export const moreItemStyle = (on: boolean) =>
  'display:flex;align-items:center;min-height:40px;padding:0 12px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'font-weight:700;color:#1D3F6B;background:rgba(79,138,214,.13);' : 'font-weight:500;color:#22272E;');

export function MoreMenu<T extends string>({
  items,
  active,
  onPick,
  tabStyle,
  minWidth = 220,
  testId,
}: {
  items: MoreItem<T>[];
  active: T;
  onPick: (id: T) => void;
  /** The row's own tab style (on = a tab in this menu is open). */
  tabStyle: (on: boolean) => string;
  minWidth?: number;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const cur = items.find((i) => i.id === active) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!items.length) return null;
  return (
    <span ref={box} data-testid={testId} style={sx('position:relative;display:inline-flex;flex:0 0 auto;')}>
      <a
        href="#"
        role="tab"
        aria-selected={!!cur}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId ? testId + '-toggle' : undefined}
        onClick={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
        style={sx(tabStyle(!!cur) + 'cursor:pointer;')}
      >
        <span>{cur ? 'More: ' + cur.label : 'More'}</span>
        <span aria-hidden="true">{' ▾'}</span>
      </a>
      {open && (
        <div
          role="menu"
          style={sx(
            'position:absolute;top:calc(100% + 4px);left:0;z-index:60;display:flex;flex-direction:column;gap:2px;min-width:' +
              minWidth +
              'px;padding:6px;border-radius:10px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);box-shadow:0 8px 24px rgba(34,39,46,.14);',
          )}
        >
          {items.map((it) => (
            <a
              key={it.id}
              href="#"
              role="menuitem"
              aria-current={it.id === active ? 'true' : undefined}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                onPick(it.id);
              }}
              style={sx(moreItemStyle(it.id === active))}
            >
              <span>{it.label}</span>
              {countOf(it)}
            </a>
          ))}
        </div>
      )}
    </span>
  );
}

function countOf<T extends string>(it: MoreItem<T>): ReactNode {
  if (!it.count) return null;
  return <span style={sx('margin-left:6px;font-size:14px;font-weight:700;color:' + (it.countColor ?? '#54606C') + ';')}>{it.count}</span>;
}
